// ─────────────────────────────────────────────────────────────────────────
// Ecoffee Cup® South Africa — shared data backbone.
//
// THREE DISTINCT DISCOVERY LAYERS (never conflate):
//   1. COLLECTIONS      official artist / heritage ranges (global brand)
//                       William Morris · Van Gogh Museum · Emma Shipley · Kew
//   2. CHOOSE YOUR STORY  South African place / mood ranges (local layer)
//                       Karoo · Cape Point · Fynbos Botanicals · Midnight Edition
//   3. SIZE & DRINK GUIDE  static sizing help (see pages/SizeGuide.tsx)
//
// Mock data only — non-functional shop.
// ─────────────────────────────────────────────────────────────────────────

export type RangeKind = 'collection' | 'story'

export type Artist = {
  name: string
  role: string
  bio: string
  portrait: string
  inspiration: string
}

/** A Range is either an official Collection or a Choose Your Story range. */
export type Range = {
  id: string
  kind: RangeKind
  name: string
  /** Collections: "Artist Series" etc. Stories: the SA region / mood. */
  series: string
  tagline: string
  story: string
  longStory: string
  image: string
  heroImage: string
  accent: string
  inspiration: string
  gallery: string[]
  artist?: Artist
  quote?: string
  quoteAttribution?: string
}

export type SizeId = '250ml' | '350ml' | '400ml'

export type Product = {
  id: string
  name: string
  rangeId: string
  rangeName: string
  rangeKind: RangeKind
  price: string
  size: SizeId
  image: string
  hoverImage: string
  color: string
  colorways: string[]
  badge?: string
  description: string
  drinks: string[]
  lifestyle: string[]
  giftThemes: string[]
  rating: number
  reviewCount: number
  bestSeller?: boolean
  newArrival?: boolean
}

// ── Artists ───────────────────────────────────────────────────────────────
const morris: Artist = {
  name: 'William Morris',
  role: 'Designer & Printmaker, 1834–1896',
  portrait:
    'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=800',
  bio: 'The defining figure of the Arts and Crafts movement, Morris believed that beautiful, well-made objects belonged in everyday life — not behind glass. His block-printed patterns remain some of the most recognisable designs ever drawn.',
  inspiration:
    'English hedgerows, medieval tapestry and the conviction that the useful thing should also be the beautiful one.',
}

const vanGogh: Artist = {
  name: 'Vincent van Gogh',
  role: 'Painter, 1853–1890 · in partnership with the Van Gogh Museum',
  portrait:
    'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=800',
  bio: 'Produced in official partnership with the Van Gogh Museum in Amsterdam, this range brings the painter’s most loved works to the daily coffee ritual — every cup supporting the museum’s conservation work.',
  inspiration:
    'Almond blossom against a spring sky, sunflowers in a stoneware jug, and a night that refuses to stay still.',
}

const shipley: Artist = {
  name: 'Emma Shipley',
  role: 'Illustrator & Textile Designer',
  portrait:
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=800',
  bio: 'Emma Shipley draws by hand in graphite before building her worlds in colour — intricate, fantastical menageries that have made her one of Britain’s most collected contemporary illustrators.',
  inspiration:
    'The animal kingdom, drawn with the patience of a naturalist and the imagination of a storyteller.',
}

const naledi: Artist = {
  name: 'Naledi Botha',
  role: 'Botanical Artist, Stellenbosch',
  portrait:
    'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=800',
  bio: 'Naledi paints the Cape Floral Kingdom in intricate detail from her garden studio, translating fynbos into living pattern.',
  inspiration:
    'The overlooked detail of the fynbos — the curl of a protea petal, the silver of a leucadendron leaf.',
}

const elena: Artist = {
  name: 'Elena van der Merwe',
  role: 'Colourist, Prince Albert',
  portrait:
    'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=800',
  bio: 'Working from a sunlit studio in the Great Karoo, Elena builds palettes from the land around her — warm in the hand, quiet in the eye.',
  inspiration:
    'The vast, silent mornings of the semi-desert — warm ochres, dusty pinks, and the long blue shadows just after dawn.',
}

// ── 1. COLLECTIONS — official artist / heritage ranges ────────────────────
export const collections: Range[] = [
  {
    id: 'william-morris',
    kind: 'collection',
    name: 'William Morris',
    series: 'Heritage Series',
    tagline: 'Pattern that never went out of print',
    story:
      'The Arts and Crafts master’s block-printed botanicals, drawn in the 1880s and still unmistakable today.',
    longStory:
      'Morris spent his life arguing that the objects we use every day deserve as much beauty as the ones we put on display. More than a century later his patterns still hold — Strawberry Thief, Golden Lily, Willow Bough — reproduced here in full colour on a cup made to be carried, not kept.',
    image:
      'https://images.unsplash.com/photo-1495474472206-bf5f28b4f461?auto=format&fit=crop&q=80&w=1200',
    heroImage:
      'https://images.unsplash.com/photo-1466781783364-36c955e42a7f?auto=format&fit=crop&q=80&w=2000',
    accent: '#284734',
    inspiration:
      'English hedgerow botanicals, block-printed in flat colour and endless repeat.',
    gallery: [
      'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1495474472206-bf5f28b4f461?auto=format&fit=crop&q=80&w=800',
    ],
    artist: morris,
    quote: 'Have nothing in your house that you do not know to be useful, or believe to be beautiful.',
    quoteAttribution: 'William Morris',
  },
  {
    id: 'van-gogh-museum',
    kind: 'collection',
    name: 'Van Gogh Museum',
    series: 'Artist Series',
    tagline: 'The paintings, on your morning',
    story:
      'An official partnership with the Van Gogh Museum, Amsterdam — Almond Blossom, Sunflowers, Starry Night.',
    longStory:
      'Made in official partnership with the Van Gogh Museum in Amsterdam, this range puts three of the most looked-at paintings in the world into the most looked-at object of your day. A share of every cup supports the museum’s conservation and education work.',
    image:
      'https://images.unsplash.com/photo-1541167760496-1628856ab772?auto=format&fit=crop&q=80&w=1200',
    heroImage:
      'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&q=80&w=2000',
    accent: '#171C8F',
    inspiration:
      'Almond blossom against spring sky, sunflower ochre, and the restless blue of a night in Saint-Rémy.',
    gallery: [
      'https://images.unsplash.com/photo-1470100698023-92c0a2a13703?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1541167760496-1628856ab772?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1497935586351-b67a49e012bf?auto=format&fit=crop&q=80&w=800',
    ],
    artist: vanGogh,
    quote: 'I dream my painting and I paint my dream.',
    quoteAttribution: 'Vincent van Gogh',
  },
  {
    id: 'emma-shipley',
    kind: 'collection',
    name: 'Emma Shipley',
    series: 'Artist Series',
    tagline: 'Hand-drawn menageries',
    story:
      'Graphite-drawn animal worlds from one of Britain’s most collected contemporary illustrators.',
    longStory:
      'Every Emma Shipley design begins as a graphite drawing — months of pencil work before a single colour is chosen. The result is a range that rewards a second look: leopards in the undergrowth, cranes mid-flight, detail hidden in detail.',
    image:
      'https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?auto=format&fit=crop&q=80&w=1200',
    heroImage:
      'https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?auto=format&fit=crop&q=80&w=2000',
    accent: '#432C47',
    inspiration:
      'The animal kingdom drawn with a naturalist’s patience and a storyteller’s imagination.',
    gallery: [
      'https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&q=80&w=800',
    ],
    artist: shipley,
  },
  {
    id: 'kew-gardens',
    kind: 'collection',
    name: 'Kew Gardens',
    series: 'Heritage Series',
    tagline: 'From the world’s great plant archive',
    story:
      'Botanical illustration drawn from the archives of the Royal Botanic Gardens, Kew.',
    longStory:
      'Kew holds one of the largest botanical archives on earth — thousands of plates drawn by hand long before photography. This range lifts a small selection of them out of the drawer and onto a cup, printed at a scale that lets the linework breathe.',
    image:
      'https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&q=80&w=1200',
    heroImage:
      'https://images.unsplash.com/photo-1495474472206-bf5f28b4f461?auto=format&fit=crop&q=80&w=2000',
    accent: '#0077C8',
    inspiration:
      'Hand-drawn botanical plates: palm house glass, orchid detail, and careful Latin annotation.',
    gallery: [
      'https://images.unsplash.com/photo-1466781783364-36c955e42a7f?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&q=80&w=800',
    ],
  },
]

// ── 2. CHOOSE YOUR STORY — South African place & mood ranges ──────────────
export const stories: Range[] = [
  {
    id: 'karoo',
    kind: 'story',
    name: 'Karoo',
    series: 'Great Karoo · Northern Cape',
    tagline: 'Warm mornings, quiet light',
    story:
      'Warm ochres and dusty pinks, drawn from the silent first hour of a semi-desert day.',
    longStory:
      'The Karoo wakes slowly. Before the heat there is a moment of stillness — the sky blushing over endless scrub, the first coffee steaming in cool air, a sheep bell somewhere far off. This is the story of that pause: a palette of warm ochre and dusty rose, made to hold in both hands on a stoep at six in the morning.',
    image:
      'https://images.unsplash.com/photo-1511537190424-bbbab87ac5eb?auto=format&fit=crop&q=80&w=1200',
    heroImage:
      'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&fit=crop&q=80&w=2000',
    accent: '#EFA6AA',
    inspiration:
      'Warm ochres, dusty pinks, and the long blue shadows of a semi-desert dawn.',
    gallery: [
      'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1442512595331-e89e73853f31?auto=format&fit=crop&q=80&w=800',
    ],
    artist: elena,
    quote:
      'You don’t rush a Karoo morning. You wait for it, and it gives you the whole day.',
    quoteAttribution: 'Elena van der Merwe, Prince Albert',
  },
  {
    id: 'cape-point',
    kind: 'story',
    name: 'Cape Point',
    series: 'Cape Peninsula · Western Cape',
    tagline: 'Where two oceans trade weather',
    story:
      'Deep maritime blues and a flash of seafoam, for salt air and a fast, bright morning.',
    longStory:
      'At the tip of the peninsula the Atlantic and the Indian trade weather all day long — sun, then wind, then sun again. Cape Point holds that meeting: saturated maritime blue, a flash of seafoam at the rim, and the clean salt smell of a coastal morning at the top of Chapman’s Peak.',
    image:
      'https://images.unsplash.com/photo-1550583724-b2692b85b150?auto=format&fit=crop&q=80&w=1200',
    heroImage:
      'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?auto=format&fit=crop&q=80&w=2000',
    accent: '#171C8F',
    inspiration:
      'Deep maritime blues, seafoam greens, and the crisp air where two oceans meet.',
    gallery: [
      'https://images.unsplash.com/photo-1559525839-b184a4d698c7?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&q=80&w=800',
    ],
  },
  {
    id: 'fynbos-botanicals',
    kind: 'story',
    name: 'Fynbos Botanicals',
    series: 'Cape Floral Kingdom · Overberg',
    tagline: 'The smallest, richest floral kingdom on earth',
    story:
      'Protea, leucadendron and silver-green texture, painted from a Stellenbosch garden studio.',
    longStory:
      'The Cape Floral Kingdom is the smallest of the world’s six — and the richest. It sits on our doorstep and most of us walk past it. Naledi Botha paints its overlooked detail: the curl of a protea bract, the silver of a leucadendron leaf, the whole mountain smelling like buchu after rain.',
    image:
      'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&q=80&w=1200',
    heroImage:
      'https://images.unsplash.com/photo-1466781783364-36c955e42a7f?auto=format&fit=crop&q=80&w=2000',
    accent: '#284734',
    inspiration:
      'Protea bracts, leucadendron silver, and the smell of the mountain after rain.',
    gallery: [
      'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1466781783364-36c955e42a7f?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&q=80&w=800',
    ],
    artist: naledi,
    quote:
      'Everything here is small and stubborn and beautiful. You have to stop walking to see it.',
    quoteAttribution: 'Naledi Botha, Stellenbosch',
  },
  {
    id: 'midnight-edition',
    kind: 'story',
    name: 'Midnight Edition',
    series: 'Limited winter release · numbered',
    tagline: 'A deep, quiet palette for the cold months',
    story:
      'Purple Pennant and matte black, released once a year in the depth of winter. Numbered.',
    longStory:
      'Released once a year and never repeated, the Midnight Edition is our most restrained story — Purple Pennant deepening into black coffee, a matte finish that reads almost like unglazed clay, and a number pressed into the base. Made for evening drinks, long tea and the last hour of a cold day. When it’s gone, it’s gone.',
    image:
      'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&q=80&w=1200',
    heroImage:
      'https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&q=80&w=2000',
    accent: '#432C47',
    inspiration:
      'Purple Pennant deepening to black, matte surfaces, and a numbered base.',
    gallery: [
      'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&q=80&w=800',
      'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=800',
    ],
  },
]

export const allRanges: Range[] = [...collections, ...stories]

// ── Products ──────────────────────────────────────────────────────────────
export const products: Product[] = [
  // William Morris
  {
    id: 'wm-strawberry-thief',
    name: 'Strawberry Thief',
    rangeId: 'william-morris',
    rangeName: 'William Morris',
    rangeKind: 'collection',
    price: 'R 399',
    size: '350ml',
    image:
      'https://images.unsplash.com/photo-1495474472206-bf5f28b4f461?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&q=80&w=800',
    color: '#284734',
    colorways: ['#284734', '#D3C9BE', '#3B302F'],
    badge: 'Bestseller',
    description:
      'The 1883 indigo-discharge print that took Morris two years to perfect, on our most popular size.',
    drinks: ['Flat white', 'Cappuccino', 'Rooibos'],
    lifestyle: ['work-from-home', 'everyday'],
    giftThemes: ['for-the-host', 'thank-you'],
    rating: 4.8,
    reviewCount: 214,
    bestSeller: true,
  },
  {
    id: 'wm-golden-lily',
    name: 'Golden Lily',
    rangeId: 'william-morris',
    rangeName: 'William Morris',
    rangeKind: 'collection',
    price: 'R 369',
    size: '250ml',
    image:
      'https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1495474472206-bf5f28b4f461?auto=format&fit=crop&q=80&w=800',
    color: '#FC9E21',
    colorways: ['#FC9E21', '#284734'],
    description:
      'Ochre and sage in tight repeat — a small, strong cup for a short, strong coffee.',
    drinks: ['Espresso', 'Cortado', 'Macchiato'],
    lifestyle: ['office', 'everyday'],
    giftThemes: ['thank-you'],
    rating: 4.7,
    reviewCount: 96,
  },
  {
    id: 'wm-willow-bough',
    name: 'Willow Bough',
    rangeId: 'william-morris',
    rangeName: 'William Morris',
    rangeKind: 'collection',
    price: 'R 429',
    size: '400ml',
    image:
      'https://images.unsplash.com/photo-1466781783364-36c955e42a7f?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&q=80&w=800',
    color: '#6E6259',
    colorways: ['#6E6259', '#284734'],
    badge: 'New',
    newArrival: true,
    description:
      'Morris’s quietest pattern on our largest cup, for the long working morning.',
    drinks: ['Americano', 'Filter', 'Large latte'],
    lifestyle: ['work-from-home', 'road-trip'],
    giftThemes: ['for-the-host'],
    rating: 4.9,
    reviewCount: 41,
  },
  // Van Gogh Museum
  {
    id: 'vg-almond-blossom',
    name: 'Almond Blossom',
    rangeId: 'van-gogh-museum',
    rangeName: 'Van Gogh Museum',
    rangeKind: 'collection',
    price: 'R 399',
    size: '350ml',
    image:
      'https://images.unsplash.com/photo-1541167760496-1628856ab772?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1470100698023-92c0a2a13703?auto=format&fit=crop&q=80&w=800',
    color: '#BCE3DF',
    colorways: ['#BCE3DF', '#D3C9BE'],
    badge: 'Bestseller',
    bestSeller: true,
    description:
      'Painted in 1890 as a gift for a newborn nephew. Still the gentlest thing in the range.',
    drinks: ['Flat white', 'Matcha', 'Green tea'],
    lifestyle: ['everyday', 'office'],
    giftThemes: ['new-beginnings', 'thank-you'],
    rating: 4.9,
    reviewCount: 302,
  },
  {
    id: 'vg-sunflowers',
    name: 'Sunflowers',
    rangeId: 'van-gogh-museum',
    rangeName: 'Van Gogh Museum',
    rangeKind: 'collection',
    price: 'R 369',
    size: '250ml',
    image:
      'https://images.unsplash.com/photo-1497935586351-b67a49e012bf?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1541167760496-1628856ab772?auto=format&fit=crop&q=80&w=800',
    color: '#FC9E21',
    colorways: ['#FC9E21', '#3B302F'],
    description:
      'Fifteen blooms in a stoneware jug, reproduced at espresso scale.',
    drinks: ['Espresso', 'Cortado'],
    lifestyle: ['office', 'commute'],
    giftThemes: ['thank-you', 'for-the-host'],
    rating: 4.6,
    reviewCount: 148,
  },
  {
    id: 'vg-starry-night',
    name: 'Starry Night',
    rangeId: 'van-gogh-museum',
    rangeName: 'Van Gogh Museum',
    rangeKind: 'collection',
    price: 'R 429',
    size: '400ml',
    image:
      'https://images.unsplash.com/photo-1470100698023-92c0a2a13703?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&q=80&w=800',
    color: '#171C8F',
    colorways: ['#171C8F', '#041E42'],
    badge: 'New',
    newArrival: true,
    description:
      'Yves-Klein blue and restless cypress on the 400ml, for the longest days.',
    drinks: ['Americano', 'Filter', 'Large latte'],
    lifestyle: ['road-trip', 'work-from-home'],
    giftThemes: ['new-beginnings'],
    rating: 4.8,
    reviewCount: 87,
  },
  // Emma Shipley
  {
    id: 'es-kruger',
    name: 'Kruger',
    rangeId: 'emma-shipley',
    rangeName: 'Emma Shipley',
    rangeKind: 'collection',
    price: 'R 399',
    size: '350ml',
    image:
      'https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?auto=format&fit=crop&q=80&w=800',
    color: '#3B302F',
    colorways: ['#3B302F', '#FC9E21'],
    description:
      'A graphite bushveld menagerie drawn over four months, printed in warm monochrome.',
    drinks: ['Flat white', 'Cappuccino'],
    lifestyle: ['road-trip', 'outdoors'],
    giftThemes: ['for-him', 'for-the-host'],
    rating: 4.7,
    reviewCount: 63,
  },
  {
    id: 'es-caspian',
    name: 'Caspian',
    rangeId: 'emma-shipley',
    rangeName: 'Emma Shipley',
    rangeKind: 'collection',
    price: 'R 369',
    size: '250ml',
    image:
      'https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?auto=format&fit=crop&q=80&w=800',
    color: '#432C47',
    colorways: ['#432C47', '#EFA6AA'],
    badge: 'New',
    newArrival: true,
    description:
      'Cranes mid-flight on Purple Pennant — hidden detail rewards a second look.',
    drinks: ['Espresso', 'Macchiato'],
    lifestyle: ['office', 'everyday'],
    giftThemes: ['for-her', 'new-beginnings'],
    rating: 4.8,
    reviewCount: 38,
  },
  // Kew Gardens
  {
    id: 'kew-palm-house',
    name: 'Palm House',
    rangeId: 'kew-gardens',
    rangeName: 'Kew Gardens',
    rangeKind: 'collection',
    price: 'R 389',
    size: '350ml',
    image:
      'https://images.unsplash.com/photo-1466781783364-36c955e42a7f?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1495474472206-bf5f28b4f461?auto=format&fit=crop&q=80&w=800',
    color: '#0077C8',
    colorways: ['#0077C8', '#BCE3DF'],
    description:
      'Glasshouse ironwork and palm frond, lifted from a nineteenth-century plate.',
    drinks: ['Latte', 'Rooibos', 'Green tea'],
    lifestyle: ['work-from-home', 'everyday'],
    giftThemes: ['for-the-host', 'thank-you'],
    rating: 4.6,
    reviewCount: 54,
  },
  {
    id: 'kew-orchid',
    name: 'Orchid Study',
    rangeId: 'kew-gardens',
    rangeName: 'Kew Gardens',
    rangeKind: 'collection',
    price: 'R 359',
    size: '250ml',
    image:
      'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1466781783364-36c955e42a7f?auto=format&fit=crop&q=80&w=800',
    color: '#EFA6AA',
    colorways: ['#EFA6AA', '#284734'],
    badge: 'Bestseller',
    bestSeller: true,
    description:
      'Hand-annotated orchid plates in Conch Shell pink on a short, sharp cup.',
    drinks: ['Espresso', 'Cortado'],
    lifestyle: ['office', 'commute'],
    giftThemes: ['for-her', 'thank-you'],
    rating: 4.7,
    reviewCount: 121,
  },
  // Karoo (story)
  {
    id: 'karoo-ochre-dawn',
    name: 'Ochre Dawn',
    rangeId: 'karoo',
    rangeName: 'Karoo',
    rangeKind: 'story',
    price: 'R 350',
    size: '350ml',
    image:
      'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1511537190424-bbbab87ac5eb?auto=format&fit=crop&q=80&w=800',
    color: '#EFA6AA',
    colorways: ['#EFA6AA', '#D3C9BE', '#3B302F'],
    badge: 'Bestseller',
    bestSeller: true,
    description:
      'The colour of the sky ten minutes before the heat arrives. Our most-carried SA cup.',
    drinks: ['Flat white', 'Rooibos', 'Cappuccino'],
    lifestyle: ['commute', 'road-trip', 'everyday'],
    giftThemes: ['for-the-host', 'thank-you'],
    rating: 4.9,
    reviewCount: 341,
  },
  {
    id: 'karoo-dust-rose',
    name: 'Dust Rose',
    rangeId: 'karoo',
    rangeName: 'Karoo',
    rangeKind: 'story',
    price: 'R 330',
    size: '250ml',
    image:
      'https://images.unsplash.com/photo-1442512595331-e89e73853f31?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?auto=format&fit=crop&q=80&w=800',
    color: '#D3C9BE',
    colorways: ['#D3C9BE', '#EFA6AA'],
    description:
      'Soft Oat Milk with a dusty pink lid — the everyday espresso cup.',
    drinks: ['Espresso', 'Macchiato'],
    lifestyle: ['office', 'everyday'],
    giftThemes: ['for-her', 'thank-you'],
    rating: 4.6,
    reviewCount: 112,
  },
  // Cape Point (story)
  {
    id: 'cape-deep-atlantic',
    name: 'Deep Atlantic',
    rangeId: 'cape-point',
    rangeName: 'Cape Point',
    rangeKind: 'story',
    price: 'R 380',
    size: '400ml',
    image:
      'https://images.unsplash.com/photo-1559525839-b184a4d698c7?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1550583724-b2692b85b150?auto=format&fit=crop&q=80&w=800',
    color: '#171C8F',
    colorways: ['#171C8F', '#BCE3DF'],
    badge: 'Bestseller',
    bestSeller: true,
    description:
      'Yves Klein blue at 400ml, built for the N2 and the long commute.',
    drinks: ['Americano', 'Large latte', 'Filter'],
    lifestyle: ['commute', 'road-trip'],
    giftThemes: ['for-him', 'new-beginnings'],
    rating: 4.8,
    reviewCount: 198,
  },
  {
    id: 'cape-seafoam',
    name: 'Seafoam',
    rangeId: 'cape-point',
    rangeName: 'Cape Point',
    rangeKind: 'story',
    price: 'R 350',
    size: '350ml',
    image:
      'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1559525839-b184a4d698c7?auto=format&fit=crop&q=80&w=800',
    color: '#BCE3DF',
    colorways: ['#BCE3DF', '#171C8F'],
    description: 'Bleached Aqua for bright coastal days and iced matcha.',
    drinks: ['Matcha', 'Iced latte', 'Green tea'],
    lifestyle: ['outdoors', 'everyday'],
    giftThemes: ['for-her', 'new-beginnings'],
    rating: 4.7,
    reviewCount: 87,
  },
  // Fynbos Botanicals (story)
  {
    id: 'fynbos-protea-green',
    name: 'Protea Green',
    rangeId: 'fynbos-botanicals',
    rangeName: 'Fynbos Botanicals',
    rangeKind: 'story',
    price: 'R 330',
    size: '250ml',
    image:
      'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1495474472206-bf5f28b4f461?auto=format&fit=crop&q=80&w=800',
    color: '#284734',
    colorways: ['#284734', '#D3C9BE'],
    badge: 'New',
    newArrival: true,
    description:
      'Dark Green with a hand-painted protea bract, at espresso size.',
    drinks: ['Espresso', 'Cortado', 'Rooibos'],
    lifestyle: ['outdoors', 'everyday'],
    giftThemes: ['for-the-host', 'thank-you'],
    rating: 4.8,
    reviewCount: 66,
  },
  {
    id: 'fynbos-leucadendron',
    name: 'Leucadendron',
    rangeId: 'fynbos-botanicals',
    rangeName: 'Fynbos Botanicals',
    rangeKind: 'story',
    price: 'R 350',
    size: '350ml',
    image:
      'https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&q=80&w=800',
    color: '#6E6259',
    colorways: ['#6E6259', '#284734'],
    description: 'Silver-green linework on Warm Gray, on the everyday 350ml.',
    drinks: ['Flat white', 'Cappuccino', 'Green tea'],
    lifestyle: ['work-from-home', 'outdoors'],
    giftThemes: ['for-the-host'],
    rating: 4.7,
    reviewCount: 74,
  },
  // Midnight Edition (story)
  {
    id: 'midnight-no1',
    name: 'Midnight No.1',
    rangeId: 'midnight-edition',
    rangeName: 'Midnight Edition',
    rangeKind: 'story',
    price: 'R 420',
    size: '350ml',
    image:
      'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&q=80&w=800',
    color: '#432C47',
    colorways: ['#432C47'],
    badge: 'Limited',
    description:
      'Matte Purple Pennant with a numbered base. Winter release, never repeated.',
    drinks: ['Flat white', 'Hot chocolate', 'Chai'],
    lifestyle: ['work-from-home', 'everyday'],
    giftThemes: ['for-him', 'for-her', 'new-beginnings'],
    rating: 5,
    reviewCount: 29,
  },
  {
    id: 'midnight-no2',
    name: 'Midnight No.2',
    rangeId: 'midnight-edition',
    rangeName: 'Midnight Edition',
    rangeKind: 'story',
    price: 'R 450',
    size: '400ml',
    image:
      'https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&q=80&w=800',
    hoverImage:
      'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&q=80&w=800',
    color: '#3B302F',
    colorways: ['#3B302F', '#432C47'],
    badge: 'Limited',
    newArrival: true,
    description:
      'Black Coffee matte at 400ml — the largest cup we make, at its quietest.',
    drinks: ['Americano', 'Filter', 'Chai'],
    lifestyle: ['road-trip', 'work-from-home'],
    giftThemes: ['for-him'],
    rating: 4.9,
    reviewCount: 18,
  },
]

// ── Sizes (metric — CI) ───────────────────────────────────────────────────
export type Size = {
  id: SizeId
  label: string
  imperial: string
  suits: string
  drinks: string[]
  lifestyle: string
  image: string
}
export const sizes: Size[] = [
  {
    id: '250ml',
    label: '250ml',
    imperial: '8oz',
    suits: 'The purist. Short, strong, no fuss.',
    drinks: ['Espresso', 'Cortado', 'Macchiato'],
    lifestyle: 'A quick one at the counter, or a focused espresso at your desk.',
    image:
      'https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?auto=format&fit=crop&q=80&w=800',
  },
  {
    id: '350ml',
    label: '350ml',
    imperial: '12oz',
    suits: 'The everyday all-rounder. Our most popular size.',
    drinks: ['Flat white', 'Cappuccino', 'Latte', 'Rooibos'],
    lifestyle: 'The daily commute, the mid-morning break, the go-anywhere cup.',
    image:
      'https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&q=80&w=800',
  },
  {
    id: '400ml',
    label: '400ml',
    imperial: '14oz',
    suits: 'The long-hauler. For big coffees and longer mornings.',
    drinks: ['Americano', 'Large latte', 'Filter', 'Chai'],
    lifestyle: 'Road trips, work-from-home marathons, slow weekend mornings.',
    image:
      'https://images.unsplash.com/photo-1442512595331-e89e73853f31?auto=format&fit=crop&q=80&w=800',
  },
]

// ── Gift themes (Gifting page → filters into Shop) ────────────────────────
export const giftThemes = [
  {
    id: 'for-the-host',
    title: 'For the Host',
    blurb: 'The friend whose kitchen everyone ends up in.',
    image:
      'https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&q=80&w=800',
  },
  {
    id: 'thank-you',
    title: 'Thank You',
    blurb: 'Teachers, neighbours, the person who fed your cat.',
    image:
      'https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=800',
  },
  {
    id: 'new-beginnings',
    title: 'New Beginnings',
    blurb: 'New job, new home, new city.',
    image:
      'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?auto=format&fit=crop&q=80&w=800',
  },
  {
    id: 'for-her',
    title: 'For Her',
    blurb: 'Softer palettes, botanical detail.',
    image:
      'https://images.unsplash.com/photo-1512389142860-9c449e58a543?auto=format&fit=crop&q=80&w=800',
  },
  {
    id: 'for-him',
    title: 'For Him',
    blurb: 'Deeper tones, bigger cups.',
    image:
      'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800',
  },
]

// ── Helpers ───────────────────────────────────────────────────────────────
export const getRange = (id?: string) => allRanges.find((r) => r.id === id)
export const getCollection = (id?: string) =>
  collections.find((c) => c.id === id)
export const getStory = (id?: string) => stories.find((s) => s.id === id)
export const getProductsForRange = (id: string) =>
  products.filter((p) => p.rangeId === id)
export const getProduct = (id?: string) => products.find((p) => p.id === id)
export const rangePath = (r: Pick<Range, 'id' | 'kind'>) =>
  r.kind === 'story' ? `/stories/${r.id}` : `/collections/${r.id}`
export const newArrivals = products.filter((p) => p.newArrival)
export const bestSellers = products.filter((p) => p.bestSeller)
export const relatedProducts = (product: Product, limit = 4) =>
  [
    ...products.filter(
      (p) => p.rangeId === product.rangeId && p.id !== product.id,
    ),
    ...products.filter(
      (p) => p.rangeId !== product.rangeId && p.size === product.size,
    ),
  ].slice(0, limit)
